// Triage summary card — Phase 2d (nurse triage portal, 2026-10-03).
//
// Rendered at the top of EncounterDetailScreen so the doctor sees the
// nurse's vitals + acuity before scrolling into the clinical note.
// Read-only snapshot — editing happens on /encounters/:id/triage (nurse-only).
//
// Shows nothing if no triage row exists yet; the "Open triage" button is
// visible for nurses so they can start one. For other roles with
// triage.view we hide the empty state (no actionable surface).

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ClinicMemberRole, EncounterTriageView, TriageAcuity } from '@pamosi/shared';
import { ApiError, clinicApi, triageApi } from '../lib/api';

const ACUITY_LABEL: Record<TriageAcuity, string> = {
    routine: 'Routine',
    urgent: 'Urgent',
    emergency: 'Emergency',
};

// Keep the colour scheme consistent with the StatusPill in EncounterDetailScreen
// — amber for a mid-urgency warning, rose for emergencies, slate for routine.
const ACUITY_CLASS: Record<TriageAcuity, string> = {
    routine: 'bg-slate-100 text-slate-700',
    urgent: 'bg-amber-100 text-amber-800',
    emergency: 'bg-rose-100 text-rose-800',
};

function canWriteTriage(role: ClinicMemberRole | null | undefined): boolean {
    return role === 'nurse' || role === 'admin';
}

interface Props {
    encounterId: string;
}

export function TriageCard({ encounterId }: Props) {
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;
    const canWrite = canWriteTriage(role);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['encounter-triage', encounterId],
        queryFn: () => triageApi.get(encounterId),
        // 404 is "no triage yet", not a real error — swallow it so the
        // card can render the empty state instead of a red banner.
        retry: (failureCount, err) => {
            if (err instanceof ApiError && err.status === 404) return false;
            return failureCount < 2;
        },
    });

    if (isLoading) {
        return (
            <div className="pm-card p-4 text-slate-500 text-sm">
                Loading triage…
            </div>
        );
    }

    const notFound = isError && error instanceof ApiError && error.status === 404;

    if (notFound || !data?.triage) {
        // Empty state: nurses see a prompt to start triage; everyone else
        // sees a muted "not yet triaged" line (the doctor still wants to
        // know the vitals haven't been captured).
        return (
            <div className="pm-card p-4 flex items-center justify-between gap-3">
                <div className="text-slate-500 text-sm">
                    No triage recorded yet.
                </div>
                {canWrite && (
                    <Link
                        to={`/encounters/${encounterId}/triage`}
                        className="pm-btn pm-btn-sm"
                    >
                        Start triage
                    </Link>
                )}
            </div>
        );
    }

    const triage = data.triage;
    return <TriageSummary triage={triage} encounterId={encounterId} canWrite={canWrite} />;
}

function TriageSummary({
    triage,
    encounterId,
    canWrite,
}: {
    triage: EncounterTriageView;
    encounterId: string;
    canWrite: boolean;
}) {
    const completed = triage.completed_at !== null;
    const nurseName = triage.nurse_full_name ?? 'Nurse';
    const stamp = new Date(triage.completed_at ?? triage.started_at).toLocaleString();
    return (
        <div className="pm-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-slate-900 font-medium text-sm">Triage</span>
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ACUITY_CLASS[triage.acuity]}`}>
                            {ACUITY_LABEL[triage.acuity]}
                        </span>
                        {!completed && (
                            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-sky-100 text-sky-800">
                                In progress
                            </span>
                        )}
                    </div>
                    <div className="text-slate-500 text-xs mt-1">
                        {nurseName} · {stamp}
                    </div>
                </div>
                {canWrite && (
                    <Link
                        to={`/encounters/${encounterId}/triage`}
                        className="pm-btn pm-btn-sm pm-btn-ghost"
                    >
                        {completed ? 'Edit triage' : 'Open triage'}
                    </Link>
                )}
            </div>

            <VitalsGrid triage={triage} />

            {triage.chief_complaint && (
                <div className="text-slate-700 text-sm">
                    <span className="text-slate-500 text-xs">Chief complaint: </span>
                    {triage.chief_complaint}
                </div>
            )}

            {triage.triage_note && (
                <div className="text-slate-700 text-sm whitespace-pre-wrap">
                    <span className="text-slate-500 text-xs">Note: </span>
                    {triage.triage_note}
                </div>
            )}
        </div>
    );
}

// Compact grid of captured vitals. We omit any null/undefined fields —
// a triage with just BP + temp shouldn't show empty cells for the rest.
function VitalsGrid({ triage }: { triage: EncounterTriageView }) {
    const items: Array<[string, string]> = [];
    if (triage.bp_systolic !== null && triage.bp_diastolic !== null) {
        items.push(['BP', `${triage.bp_systolic}/${triage.bp_diastolic} mmHg`]);
    }
    if (triage.heart_rate !== null) items.push(['HR', `${triage.heart_rate} bpm`]);
    if (triage.resp_rate !== null) items.push(['RR', `${triage.resp_rate}/min`]);
    if (triage.temp_c !== null) items.push(['Temp', `${triage.temp_c}°C`]);
    if (triage.spo2 !== null) items.push(['SpO₂', `${triage.spo2}%`]);
    if (triage.weight_kg !== null) items.push(['Weight', `${triage.weight_kg} kg`]);
    if (triage.height_cm !== null) items.push(['Height', `${triage.height_cm} cm`]);
    if (triage.pain_score !== null) items.push(['Pain', `${triage.pain_score}/10`]);

    if (items.length === 0) {
        return (
            <div className="text-slate-400 text-xs italic">No vitals captured yet.</div>
        );
    }

    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
            {items.map(([label, value]) => (
                <div key={label}>
                    <div className="text-slate-500 text-xs">{label}</div>
                    <div className="text-slate-800 text-sm font-medium">{value}</div>
                </div>
            ))}
        </div>
    );
}
